"use client";

import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronRight } from "lucide-react";
import Image from "next/image";

import { useTranslations } from "next-intl";

type Game = {
    name: string;
    slug: string;
    logo: string;
    bg: string;
    tagline: string;
    startingPrice: number;
    tags: string[];
    accentColor: string;
};

function GameCard({ game }: { game: Game }) {
    const t = useTranslations('Games');
    return (
        <a
            href={`/${game.slug}`}
            className="group relative rounded-2xl overflow-hidden block w-full"
            style={{ aspectRatio: "16/9" }}
        >
            <Image
                src={game.bg}
                alt={game.name}
                fill
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                className="object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
            <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ boxShadow: `inset 0 0 60px ${game.accentColor}22` }}
            />

            
            <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-1 group-hover:translate-y-0 transition-transform duration-300">
                <div className="flex items-end justify-between gap-2">
                    <div>
                        <p className="text-white/50 font-poppins text-[10px] uppercase tracking-widest mb-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-75">
                            {t('starting_from', { price: game.startingPrice })}
                        </p>
                        <h3 className="text-white font-akira text-lg leading-tight drop-shadow-lg">{game.name}</h3>
                        <p className="text-white/40 font-poppins text-xs mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-100">
                            {game.tagline}
                        </p>
                    </div>
                    <div className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-full border border-white/30 bg-white/10 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-2 group-hover:translate-x-0">
                        <ChevronRight className="w-4 h-4 text-white" />
                    </div>
                </div>
            </div>

            
            <div className="absolute top-4 left-4 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-75">
                {game.tags.map((tag) => (
                    <span
                        key={tag}
                        className="text-[10px] font-poppins font-medium px-2 py-0.5 rounded-full bg-black/50 backdrop-blur-sm text-white/70 border border-white/10"
                    >
                        {tag}
                    </span>
                ))}
            </div>
        </a>
    );
}

export default function GamesSection() {
    const t = useTranslations('Games');

    const games: Game[] = [
        {
            name: "Minecraft",
            slug: "minecraft-servers",
            logo: "https://cdn.aurex.lk/games/mc-logo.webp",
            bg: "https://cdn.aurex.lk/games-background/minecraft-keyart.webp",
            tagline: t('tagline_mc'),
            startingPrice: 400,
            tags: ["Sandbox", "Survival", "Multiplayer"],
            accentColor: "#8B5CF6",
        },
        {
            name: "Hytale",
            slug: "hytale-servers",
            logo: "https://cdn.aurex.lk/games/hytale.webp",
            bg: "https://cdn.aurex.lk/games-background/hytale-keyart.webp",
            tagline: t('tagline_hytale'),
            startingPrice: 2400,
            tags: ["Adventure", "RPG", "Sandbox"],
            accentColor: "#f97316",
        },
        {
            name: "MTA",
            slug: "mta-servers",
            logo: "https://cdn.aurex.lk/games/mta.webp",
            bg: "https://cdn.aurex.lk/games-background/mta-back.webp",
            tagline: t('tagline_mta'),
            startingPrice: 400,
            tags: ["Action", "Roleplay", "Sandbox"],
            accentColor: "#3b82f6",
        },
        {
            name: "FiveM",
            slug: "fivem-servers",
            logo: "https://cdn.aurex.lk/fivem/fm-left.webp",
            bg: "https://cdn.aurex.lk/fivem/fm-back.webp",
            tagline: t('tagline_fivem'),
            startingPrice: 1600,
            tags: ["Action", "Roleplay", "Sandbox"],
            accentColor: "#f97316",
        },
        {
            name: "Rust",
            slug: "rust-servers",
            logo: "https://cdn.aurex.lk/rust/rust-left.webp",
            bg: "https://cdn.aurex.lk/rust/rust-bg.webp",
            tagline: t('tagline_rust'),
            startingPrice: 2400,
            tags: ["Survival", "Multiplayer", "PVP"],
            accentColor: "#dc2626",
        },
        {
            name: "Valheim",
            slug: "valheim-servers",
            logo: "https://dash.aurex.lk/storage/categories/wQiHwHqov1kyZqKKPa8I3aDA6Nxba0PZTbvnfFhW.png",
            bg: "https://cdn.aurex.lk/valheim/valheim-bg.webp",
            tagline: t('tagline_valheim'),
            startingPrice: 1600,
            tags: ["Survival", "Multiplayer", "Sandbox"],
            accentColor: "#0891b2",
        },
    ];

    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(true);
    const [isFirst, setIsFirst] = useState(true);
    const [isLast, setIsLast] = useState(false);

    const [emblaRef, emblaApi] = useEmblaCarousel({
        loop: false,
        align: "center",
        slidesToScroll: 1,
        containScroll: "keepSnaps",
    });

    const scrollPrev = useCallback(() => { if (emblaApi) emblaApi.scrollPrev(); }, [emblaApi]);
    const scrollNext = useCallback(() => { if (emblaApi) emblaApi.scrollNext(); }, [emblaApi]);

    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        const index = emblaApi.selectedScrollSnap();
        setIsFirst(index === 0);
        setIsLast(index === games.length - 1);
        setCanScrollPrev(emblaApi.canScrollPrev());
        setCanScrollNext(emblaApi.canScrollNext());
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        onSelect();
        emblaApi.on("select", onSelect);
        emblaApi.on("reInit", onSelect);
        return () => {
            emblaApi.off("select", onSelect);
            emblaApi.off("reInit", onSelect);
        };
    }, [emblaApi, onSelect]);

    const maskImage = isFirst && isLast
        ? "none"
        : isFirst
            ? "linear-gradient(to right, black 92%, transparent 100%)"
            : isLast
                ? "linear-gradient(to right, transparent 0%, black 8%)"
                : "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)";

    return (
        <section className="relative z-10 pb-24 bg-black overflow-hidden">
            
            <div className="max-w-7xl mx-auto px-6 mb-10 flex items-end justify-between">
                <div>
                    <p className="text-purple-400 font-poppins text-xs uppercase tracking-widest mb-2">{t('subtitle')}</p>
                    <h2 className="font-akira text-white text-2xl md:text-3xl leading-tight">
                        {t('title')}
                    </h2>
                </div>
                <a
                    href="/games"
                    className="hidden sm:flex items-center gap-1.5 text-sm font-poppins text-gray-400 hover:text-white transition-colors group"
                >
                    {t('view_all')}
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </a>
            </div>

            
            <div className="relative max-w-7xl mx-auto px-6">
                <button
                    aria-label="Previous slide"
                    onClick={scrollPrev}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-20 flex h-10 w-10 items-center justify-center rounded-xl border-2 border-purple-400/70 backdrop-blur-xl bg-black/60 transition-all hover:bg-black/80"
                    style={{ visibility: canScrollPrev ? "visible" : "hidden" }}
                >
                    <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M15 18l-6-6 6-6" />
                    </svg>
                </button>
                <button
                    aria-label="Next slide"
                    onClick={scrollNext}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-20 flex h-10 w-10 items-center justify-center rounded-xl border-2 border-purple-400/70 backdrop-blur-xl bg-black/60 transition-all hover:bg-black/80"
                    style={{ visibility: canScrollNext ? "visible" : "hidden" }}
                >
                    <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M9 18l6-6-6-6" />
                    </svg>
                </button>

                <div
                    ref={emblaRef}
                    style={{
                        overflow: "hidden",
                        maskImage,
                        WebkitMaskImage: maskImage,
                    }}
                >
                    <div className="flex gap-4">
                        {games.map((game) => (
                            <div
                                key={game.slug}
                                className="flex-[0_0_88%] sm:flex-[0_0_55%] lg:flex-[0_0_48%] min-w-0"
                            >
                                <GameCard game={game} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
